import { useCallback, useEffect, useRef, useState } from 'react';

import axios from 'axios';

import { deleteRequest, get, post, put } from 'helpers/api';
import { RECORD_CHANGED_EVENT } from 'helpers/record_changed';

import { refreshActivities } from 'bundles/apex/activities/hooks';

import type { Attachment, Task, TaskAttributes } from 'bundles/apex/tasks/types';
import type { RecordChange, RecordChangeKind } from 'helpers/record_changed';
import type { ApiFilters } from './filters/types';
import type { Pagination } from './context/tasks_context';

export const TASK_CHANGE_KINDS: RecordChangeKind[] = ['task_created', 'task_updated', 'task_deleted'];

type Paths = {
  create?: string;
  index?: string;
}

function deadlineTime(task: Task): number | null {
  const date = task.deadline?.date;
  return date ? new Date(date).getTime() : null;
}

function sortsBefore(task: Task, other: Task, descending: boolean): boolean {
  const taskTime = deadlineTime(task);
  const otherTime = deadlineTime(other);

  if (taskTime === null) return false;
  if (otherTime === null) return true;
  return descending ? taskTime > otherTime : taskTime < otherTime;
}

// TODO: Clean up the task component so it makes its own create/update/delete calls
//       Serialization can move into the create/update calls then too, instead of happening in the modal
// TODO: Eliminate updateTask, replaceTask, addTaskAttachment, removeTaskAttachment
//       Instead, task components should manage their own internal state to keep this a simpler hook
//       that just wraps a collection
//
// Filters are required, but can start as null to allow for them to be set on mount
export function useTasksState(
  { paginate, paths }: {
    paginate?: boolean;
    paths: Paths;
  }
) {
  const [filters, _setFilters] = useState<ApiFilters | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [page, _setPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination | null>(null);

  const loadingRef = useRef(false);
  const refreshQueuedRef = useRef(false);

  const setFilters = useCallback((filters: ApiFilters) => {
    _setFilters(filters);
    _setPage(1);
    loadTasks(filters, 1);
  }, []);

  const setPage = useCallback(async (page: number) => {
    _setPage(page);

    let tasks: Task[] = [];
    if (filters) {
      tasks = await loadTasks(filters, page);
    }

    return tasks;
  }, [filters]);

  const loadTasks = useCallback(async (filters: ApiFilters, page: number, { quiet = false } = {}): Promise<Task[]> => {
    if (quiet && loadingRef.current) {
      refreshQueuedRef.current = true;
      return [];
    }

    if (!quiet) {
      loadingRef.current = true;
      setLoading(true);
    }

    const path = paths.index || '/apex/tasks';
    const data = await getTasks({ path, filters, page: paginate ? page : null });
    if (!data) return [];

    loadingRef.current = false;
    setLoading(false);

    let tasks: Task[];
    if (paginate) {
      const lastPage: number = data.meta.totalPages;
      if (lastPage > 0 && page > lastPage) {
        _setPage(lastPage);
        return loadTasks(filters, lastPage, { quiet });
      }

      setPagination(data.meta);
      tasks = data.tasks;
    } else {
      tasks = data;
    }

    setTasks(tasks);

    if (refreshQueuedRef.current) {
      refreshQueuedRef.current = false;
      loadTasks(filters, page, { quiet: true }).catch(() => {});
    }

    return tasks;
  }, []);

  // Quietly, so the rows stay on screen rather than giving way to a spinner while the list catches up
  useEffect(() => {
    if (!filters) return;

    const onRecordChanged = (event: Event) => {
      const change = (event as CustomEvent<RecordChange>).detail;
      if (!change || !TASK_CHANGE_KINDS.includes(change.kind)) return;

      // A refresh that fails leaves the rows already on screen, so there is nothing to report
      loadTasks(filters, page, { quiet: true }).catch(() => {});
    };

    window.addEventListener(RECORD_CHANGED_EVENT, onRecordChanged);
    return () => window.removeEventListener(RECORD_CHANGED_EVENT, onRecordChanged);
  }, [filters, page]);

  const reloadTasks = useCallback(() => {
    if (filters) {
      loadTasks(filters, page);
    }
  }, [filters, page]);

  const createTask = useCallback(async (task: TaskAttributes) => {
    const savedTask = await post(paths.create, task);
    setTasks(tasks => [savedTask, ...tasks]);
    setSelectedTask(savedTask);
    return savedTask;
  }, []);

  const updateTask = useCallback(async (task: TaskAttributes) => {
    const savedTask = await put(task.path, task);
    if (savedTask.createdActivityId) {
      refreshActivities({ fresh: true });
    }
    replaceTask(savedTask);
    return savedTask;
  }, []);

  const replaceTask = useCallback((updatedTask: Task) => {
    setTasks(tasks => tasks.map(task => task.id === updatedTask.id ? updatedTask : task));
    setSelectedTask(updatedTask);
  }, []);

  // used to re-add an undone (complete back to incomplete) completion of task
  const addTask = useCallback((task: Task) => {
    setTasks((tasks) => {
      if (tasks.some(existing => existing.id === task.id)) {
        return tasks.map(existing => existing.id === task.id ? task : existing);
      }

      const descending = filters?.sortOrder === 'desc';
      const insertAt = tasks.findIndex(existing => sortsBefore(task, existing, descending));
      return insertAt === -1
        ? [...tasks, task]
        : [...tasks.slice(0, insertAt), task, ...tasks.slice(insertAt)];
    });
  }, [filters?.sortOrder]);

  const removeTaskFromList = useCallback((task: Task) => {
    setTasks(tasks => tasks.filter(t => t.id !== task.id));
    if (selectedTask?.id === task.id) {
      setSelectedTask(null);
    }
  }, [selectedTask]);

  const deleteTask = useCallback(async (task: Task) => {
    const deletedTask = await deleteRequest(task.path);
    removeTaskFromList(deletedTask);
  }, [removeTaskFromList]);

  const addTaskAttachment = useCallback((task: Task, attachment: Attachment) => {
    replaceTask({
      ...task,
      attachments: [...task.attachments, attachment],
    });
  }, []);

  const removeTaskAttachment = useCallback((task: Task, attachment: Attachment) => {
    replaceTask({
      ...task,
      attachments: task.attachments.filter(taskAttachment => taskAttachment.id !== attachment.id),
    });
  }, []);

  return {
    selectedTask,
    setSelectedTask,
    tasks,

    setFilters,

    loading,
    setLoading, // Useful for debounced inputs to be allowed to pre-set the loading state

    pagination,
    setPage,

    addTask,
    addTaskAttachment,
    createTask,
    deleteTask,
    reloadTasks,
    removeTaskAttachment,
    removeTaskFromList,
    replaceTask,
    updateTask,
  };
}

let tasksAbortController: AbortController | null = null;
async function getTasks({ path, filters, page }: { path: string, filters: ApiFilters, page: number | null }) {
  tasksAbortController?.abort();
  tasksAbortController = new AbortController();

  try {
    return await get(path, {
      signal: tasksAbortController.signal,
      params: { ...filters, include: ['parent'], page },
    });
  } catch (error) {
    if (axios.isCancel(error)) {
      return null;
    }
    throw error;
  }
}

export type TaskCounts = {
  total: number;
  incomplete: number;
  overdue: number;
  completed: number;
  today: number;
  week: number;
  month: number;
  noDate: number;
};

type CountFilters = Pick<
  ApiFilters,
  'allTasks' | 'assigneeId' | 'officeId' | 'parentTypeFilter' | 'query' | 'tbd' | 'type'
>;

let countsAbortController: AbortController | null = null;
export async function getTaskCounts(
  { path, filters }: { path: string, filters: CountFilters }
): Promise<TaskCounts | null> {
  countsAbortController?.abort();
  countsAbortController = new AbortController();

  try {
    return await get(path, {
      signal: countsAbortController.signal,
      params: filters,
    });
  } catch (error) {
    if (axios.isCancel(error)) {
      return null;
    }
    throw error;
  }
}
